import { useEffect, useRef, useState } from 'react'
import { Box, Flex, Container, Link } from 'theme-ui'
import { Formik, Form, FormikProps } from 'formik'
import { RouteComponentProps, useLocation } from 'react-router-dom'
import {
  Nav,
  Paper,
  Heading,
  Text,
  Button,
  RouterLink,
  Loading,
} from 'components'
import {
  ErrorMessage,
  LoginFormSchema,
  InputWithAdornment,
} from 'components/Form'
import { useAuth } from 'providers/auth'
import { hasLocalStorage } from 'utils'
import { scrollToEnd } from 'utils/helpers'
import getLocalized from 'i18n'
import { LocationState } from 'types'
const localized = getLocalized()

type FormValues = {
  email: string
}

export function Login({ history }: RouteComponentProps) {
  const location = useLocation<LocationState>()
  useEffect(() => {
    document.title = localized.documentTitles.adminDashboard
  }, [])
  const [validEmail, setValidEmail] = useState<string | undefined>(undefined)
  const [invalidUserError, setInvalidUserError] = useState<string | undefined>(
    undefined
  )
  const inputRef = useRef<HTMLInputElement>(null)
  const [inputFocused, setInputFocused] = useState<boolean>(false)

  const focusedButtonStyle = {
    borderColor: 'var(--theme-ui-colors-primary,#ffc436)',
    boxShadow: '0 0 0 2px var(--theme-ui-colors-primary,#ffc436)',
    outline: 'none',
  } as any
  const blurredButtonStyle = {
    boxShadow: 'none',
  } as any

  const buttonStyle = {
    transition: '200ms ease-in-out',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    position: 'absolute',
    right: -180,
    top: -25,
    minWidth: 180,
    height: 50,
    borderRadius: 8,
    borderTopLeftRadius: 0,
    borderBottomLeftRadius: 0,
    '&:hover:before': {
      opacity: 0,
    },
    ...(inputFocused ? focusedButtonStyle : blurredButtonStyle),
  } as any

  const [pending, setPending] = useState<boolean>(false)
  const { login } = useAuth()

  const handleInputFocus = () => {
    setInvalidUserError(undefined)
    setInputFocused(true)
  }
  const handleInputBlur = () => {
    setInputFocused(false)
  }
  const handleOnSubmit = async (values: FormValues) => {
    const cleanedEmail = values.email.trim().toLocaleLowerCase()
    setPending(true)

    try {
      await login(cleanedEmail)
      setValidEmail(cleanedEmail)
      setPending(false)
      setInvalidUserError(undefined)
      if (!hasLocalStorage()) {
        history.push({
          pathname: '/verify',
          state: { ...location?.state, cleanedEmail },
        })
      }
    } catch (err: any) {
      setInvalidUserError(err.message)
      setPending(false)
    }
  }

  return (
    <>
      <Nav />
      <Box p={2} bg="primary" sx={{ height: '80vh' }}>
        <Container
          data-cy="login-screen"
          sx={{ display: 'flex', flex: 1, height: '100%' }}>
          <Flex
            sx={{
              justifyContent: 'center',
              alignItems: 'center',
              flex: 1,
            }}>
            <Paper
              sx={{
                textAlign: 'center',
                minHeight: 330,
                width: '100%',
                maxWidth: 780,
              }}>
              <Heading
                data-cy="header"
                variant="text.h1"
                as="h1"
                sx={{ mt: 4 }}>
                Log in to the TM App Admin Dashboard
              </Heading>
              <Flex
                sx={{
                  minHeight: 155,
                  alignItems: 'center',
                  flexDirection: 'column',
                }}>
                {validEmail && (
                  <Flex
                    sx={{
                      pt: 3,
                      flex: 1,
                      alignItems: 'center',
                      justifyContent: 'center',
                      flexDirection: 'column',
                      maxWidth: 400,
                    }}>
                    <Heading variant="text.h2" data-cy="email-success-message">
                      Check your inbox
                    </Heading>
                    <Text variant="text.body">
                      To log in to your account, click the link in the email
                      that was sent to: <strong>{validEmail}</strong>.
                    </Text>
                  </Flex>
                )}
                {!validEmail && (
                  <Box sx={{ flex: 1, minHeight: 140 }}>
                    <Text mt={4}>
                      Please enter your TM App email. We'll send you a link so
                      you can log in.
                    </Text>
                    <Formik
                      initialValues={{ email: '' }}
                      onSubmit={handleOnSubmit}
                      validationSchema={LoginFormSchema}>
                      {({
                        errors,
                        touched,
                        values,
                        setFieldValue,
                        handleSubmit,
                      }: FormikProps<FormValues>) => {
                        const handleChange = (evt: any) => {
                          const { name, value } = evt.target
                          const processedValue = value.trim()
                          scrollToEnd(inputRef)
                          setFieldValue(name, processedValue)
                        }
                        return (
                          <Form onSubmit={handleSubmit}>
                            <Flex
                              mt={5}
                              mb={3}
                              mx="auto"
                              sx={{
                                position: 'relative',
                                maxWidth: 620,
                              }}>
                              <InputWithAdornment
                                data-cy="email-input"
                                type="text"
                                name="email"
                                ref={inputRef}
                                placeholder="Enter your TM App email"
                                value={values.email}
                                disabled={pending}
                                onFocus={handleInputFocus}
                                onBlur={handleInputBlur}
                                aria-invalid={
                                  errors.email && touched.email ? true : false
                                }
                                sx={{
                                  borderTopRightRadius: 0,
                                  borderBottomRightRadius: 0,
                                  transition: '200ms ease-in-out',
                                }}
                                after={
                                  <Button
                                    data-cy="email-submit"
                                    type="submit"
                                    tabIndex={2}
                                    sx={buttonStyle}
                                    disabled={pending}>
                                    {pending && <Loading />}
                                    {!pending && <span>Send link</span>}
                                  </Button>
                                }
                                isAfterClickable={true}
                                tabIndex={1}
                                onChange={handleChange}
                              />
                            </Flex>
                            <ErrorMessage>
                              {errors.email && touched.email && (
                                <span>{errors.email}</span>
                              )}
                              {invalidUserError && (
                                <span data-cy="email-invalid-error">
                                  {invalidUserError === 'Not authorized.' ? (
                                    <>
                                      We're sorry! It looks like you do not have
                                      access.
                                      <br />
                                      Contact{' '}
                                      <Link
                                        variant="text.warning"
                                        href="mailto:teachers@tm.community">
                                        support
                                      </Link>
                                    </>
                                  ) : (
                                    invalidUserError
                                  )}
                                </span>
                              )}
                            </ErrorMessage>
                          </Form>
                        )
                      }}
                    </Formik>
                  </Box>
                )}
              </Flex>

              {validEmail ? (
                <RouterLink
                  variant="links.router"
                  to={{
                    pathname: '/verify',
                    state: { ...location.state, validEmail },
                  }}>
                  <span>Enter verification code manually</span>
                </RouterLink>
              ) : (
                <Link
                  data-cy="lost-email"
                  href="mailto:teachers@tm.community"
                  variant="links.router">
                  <span>I don’t know my TM App email</span>
                </Link>
              )}
            </Paper>
          </Flex>
        </Container>
      </Box>
    </>
  )
}
